import React from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Button from "@components/Button";
import Spinner from "@components/Spinner";

const Root = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 28px 24px;
  border-radius: 10px;
  text-align: center;
  width: 320px;
  max-width: calc(100% - 32px);
  box-shadow: 0 6px 24px rgba(73, 76, 91, 0.18);
  z-index: 3;
`;

const Title = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 10px;
`;

const Text = styled.p`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #899093;
  margin: 0 0 20px 0;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Progress = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #494c5b;
  margin-bottom: 4px;
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #eef1f0;
  overflow: hidden;
  margin: 12px 0 4px 0;
`;

const Fill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: #68ad18;
  transition: width 0.4s ease;
`;

const ProductivityEmptyState: React.FC = () => {
  const vm = useFieldsScreenVM();

  if (vm.selectedFieldView !== "productivity" || vm.selectedFieldId == null) return null;
  // Map is built and rendered — nothing to overlay.
  if (vm.productivityReady) return null;

  const job = vm.productivityJob;
  const status = job?.status;
  const isGenerating = status === "pending" || status === "running";
  const isFailed = status === "failed";

  // Still fetching the stored map (before we know whether one exists).
  if (vm.productivityLoading && !job) {
    return (
      <Root>
        <SpinnerWrap>
          <Spinner size={32} />
        </SpinnerWrap>
        <Text>Loading productivity map…</Text>
      </Root>
    );
  }

  if (isGenerating) {
    const pct = Math.max(0, Math.min(100, job?.progress ?? 0));
    return (
      <Root>
        <SpinnerWrap>
          <Spinner size={32} />
        </SpinnerWrap>
        <Title>Building productivity map</Title>
        <Progress>{job?.stage ?? "Processing satellite history…"}</Progress>
        <Bar>
          <Fill pct={pct} />
        </Bar>
        <Text style={{ margin: "8px 0 0 0" }}>
          {pct}% — this can take a few minutes. You can leave this page; it keeps running.
        </Text>
      </Root>
    );
  }

  const handleGenerate = () => {
    vm.startProductivityGeneration(String(vm.selectedFieldId));
  };

  if (isFailed) {
    return (
      <Root>
        <Title>Couldn't build the map</Title>
        <Text>{job?.error ?? "Something went wrong while processing satellite history."}</Text>
        <Button kind="primary" onClick={handleGenerate}>
          Try again
        </Button>
      </Root>
    );
  }

  // Default: no data yet.
  return (
    <Root>
      <Title>Productivity Map</Title>
      <Text>
        See which parts of this field consistently perform well or poorly, built from
        every clear satellite image since 2017. We process the history once and store it,
        so it opens instantly next time.
      </Text>
      <Button kind="primary" onClick={handleGenerate}>
        Download historical data
      </Button>
    </Root>
  );
};

export default observer(ProductivityEmptyState);
